<script setup>
import { ref, computed } from "vue";
import { descargarDatosBancarios } from "@/utils/components";

const baseUrl = process.env.VUE_APP_SISTEMA_SUBASTAS_ACTUAL_URL

const emit = defineEmits(["informar-garantia"]);

const nf = new Intl.NumberFormat("es-AR");

function toNumberFromArCurrency(v) {
  if (!v) return 0;
  const onlyDigits = String(v).replace(/[^\d]/g, "");
  return Number(onlyDigits || 0);
}
function formatArCurrency(v) {
  return v <= 0 ? "" : `$ ${nf.format(v)}`;
}

const garantiaInput = ref(null);

const garantia = computed(() => toNumberFromArCurrency(garantiaInput.value));

const credito = computed(() => garantia.value * 10);
const esIlimitado = computed(() => garantia.value >= 10000000);

const creditoLabel = computed(() =>
  esIlimitado.value ? "SIN TOPE" : `$ ${nf.format(credito.value)}`
);

function onInput(e) {
  const num = toNumberFromArCurrency(e.target.value);
  garantiaInput.value = formatArCurrency(num);
}
</script>

<template>
  <div class="row g-3">
    <div class="col-12 col-lg-6 d-flex">
      <div class="card shadow-sm border-0 uc-card h-100 w-100">
        <div class="card-body">
          <h5 class="fw-bold mb-2 text-primary">
            <i class="fa-solid fa-calculator me-2"></i>
            Calculadora de Crédito
          </h5>

          <p class="text-body-secondary mb-2">
            Para participar, tenés que depositar una garantía equivalente al
            <strong>10%</strong> del monto máximo de crédito que quieras para
            comprar.
          </p>

          <p class="text-body-secondary small mb-3 pb-6">
            <em>
              Ejemplo: si ingresás como garantía $200.000 tu crédito para
              comprar será de $2.000.000.
            </em>
            <br />
            <strong>A partir de $5.000.000, el crédito es ilimitado.</strong>
          </p>

          <div
            class="d-flex align-items-center gap-2 mb-2"
            v-if="garantiaInput"
          >
            <span class="text-body-secondary">Crédito de compra:</span>
            <span
              class="badge bg-soft-green ms-3 pb-1 pt-1"
              style="font-size: 0.95rem"
            >
              {{ creditoLabel }}
            </span>
          </div>

          <input
            class="garantia-input border-primary form-control form-control-lg mb-3"
            inputmode="numeric"
            autocomplete="off"
            placeholder="Ingresá el valor de tu garantía"
            v-model="garantiaInput"
            @input="onInput"
          />
        </div>
      </div>
    </div>

    <div class="col-12 col-lg-6 d-flex">
      <div class="card shadow-sm border-0 uc-card uc-card-howto h-100 w-100">
        <div class="card-body">
          <h5 class="fw-bold mb-3 text-primary">¿Cómo participar?</h5>

          <ol class="list-unstyled mb-0">
            <li class="mb-3">
              <div>
                <span class="fw-bold text-dark">1. Ingresá la Garantía</span>
              </div>
              <p class="small text-body-secondary mb-1">
                Puede ser por transferencia bancaria, eCheq o depósito.
              </p>

              <button
                type="button"
                class="btn btn-link p-0 howto-link d-inline-flex align-items-center"
                @click="descargarDatosBancarios"
              >
                <i class="fas fa-download me-2"></i>
                <span class="howto-link-text text-primary">Descargar datos bancarios.</span>
              </button>
            </li>

            <li class="mb-3">
              <div>
                <span class="fw-bold text-dark">2. Cargá el comprobante</span>
              </div>Preguntas
              <p class="small text-body-secondary mb-1">
                Completá los campos solicitados una vez realizada la operación y
                adjuntá el comprobante.
              </p>

              <button
                type="button"
                class="btn btn-link p-0 howto-link"
                @click="emit('informar-garantia')"
              >
                <span class="howto-link-text text-primary">Informar garantía.</span>
              </button>
            </li>

            <li class="mb-2">
              <div>
                <span class="fw-bold text-dark">3. Esperá la habilitación</span>
              </div>
              <p class="small text-body-secondary mb-0">
                Una vez acreditada, ya estás
                <strong> habilitado para ofertar.</strong>
              </p>
            </li>
          </ol>

          <p class="small text-body-secondary mt-3 mb-0">
            ¿Tenés dudas? Visitá nuestras
            <a :href="`${baseUrl}/subastas/preguntas-frecuentes`" class="howto-link howto-link-text text-primary">
              Preguntas Frecuentes.
            </a>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.garantia-input {
  border-radius: 6px !important;
  border-width: 2px !important;
}

.uc-card {
  border-radius: 14px;
}

.text-soft-green {
  color: #006c4c;
}

.badge {
  font-weight: 700;
}

.bg-soft-green {
  background-color: rgba(0, 108, 76, 0.11);
  color: #006c4c;
}

.howto-link {
  color: #006c4c !important;
  font-weight: 600;
  text-decoration: none;
}

.howto-link-text {
  color: #006c4c;
  text-decoration: underline;
}

.howto-link:hover .howto-link-text,
a.howto-link-text:hover {
  color: #004f37;
  text-decoration: underline;
}
</style>
